import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/common/DashboardLayout';
import AdminNav from '../../components/admin/AdminNav';
import RichTextEditor from '../../components/common/RichTextEditor';
import MentionInput from '../../components/common/MentionInput';
import MultiImageDropZone from '../../components/common/MultiImageDropZone';
import { renderRichText } from '../../utils/richText';
import * as todoService from '../../services/adminTodo.service';

const RECURRING_LABELS = {
  daily_email_post:      'Daily · post the daily email content',
  daily_announcement:    'Daily · post the Announcement',
  weekly_member_checkin: 'Daily · connect with a lapsed member',
};

const PRIORITY_ORDER = { high: 0, normal: 1, low: 2 };
const PRIORITY_BADGE = {
  high: 'bg-red-50 text-red-600',
  low:  'bg-gray-100 text-gray-400',
};
const SORTERS = {
  due: (a, b) => (a.due_date === null) - (b.due_date === null) || (a.due_date || '').localeCompare(b.due_date || '') || b.created_at.localeCompare(a.created_at),
  priority: (a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority] || SORTERS.due(a, b),
  newest: (a, b) => b.created_at.localeCompare(a.created_at),
};

function isOverdue(todo) {
  if (!todo.due_date || todo.completed_at) return false;
  return new Date(todo.due_date) < new Date(new Date().toDateString());
}

function formatDue(dateStr) {
  if (!dateStr) return null;
  // Postgres DATE columns come back through JSON as a full ISO datetime
  // ("2026-08-26T00:00:00.000Z"), not the plain "2026-08-26" this assumed —
  // appending another 'T00:00:00' onto that produced an unparseable string
  // that rendered as the literal text "Invalid Date". Taking just the date
  // portion handles both that shape and a plain date string the same way.
  return new Date(dateStr.slice(0, 10) + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export default function AdminTodos() {
  const [active, setActive] = useState(null);
  const [archived, setArchived] = useState([]);
  const [snoozed, setSnoozed] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [showSnoozed, setShowSnoozed] = useState(false);
  // Jenn, 2026-09-18 (returning bug): completed items hide by default.
  const [hideCompleted, setHideCompleted] = useState(true);
  // Jenn, 2026-09-12: "Can our To Do's have a different starting view?" —
  // defaulting to Priority now that new approval-type to-dos correctly
  // default to High (see admin_todos: ADMIN_SEND... / bulk-email flows),
  // so the most urgent real item actually surfaces first without having to
  // remember to switch sorts.
  const [sortBy, setSortBy] = useState('priority');
  const [error, setError] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [highlightId, setHighlightId] = useState(() => new URLSearchParams(window.location.search).get('highlight'));
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      setError(null);
      const { active, archived, snoozed } = await todoService.getTodos(showArchived);
      setActive(active);
      setArchived(archived);
      setSnoozed(snoozed || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load to-dos.');
    }
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  // Deep-link from a "You've been mentioned" notification — the link points
  // at ?highlight=<id> since the to-do list has no per-item page of its own.
  // Jenn, 2026-08-27: clicking a mention "brings me to the full page of
  // to-do's. But not to the open to-do...so I'd have to search for it."
  useEffect(() => {
    if (!highlightId || !active) return;
    const match = active.find(t => t.id === highlightId);
    if (!match) return;
    if (match.completed_at && hideCompleted) { setHideCompleted(false); return; }
    const el = document.getElementById(`todo-${highlightId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const url = new URL(window.location.href);
    url.searchParams.delete('highlight');
    window.history.replaceState({}, '', url);
    const t = setTimeout(() => setHighlightId(null), 4000);
    return () => clearTimeout(t);
  }, [highlightId, active, hideCompleted]);

  const handleToggleComplete = async (todo) => {
    try {
      const updated = await todoService.setTodoCompleted(todo.id, !todo.completed_at);
      setActive(prev => prev.map(t => t.id === updated.id ? { ...t, ...updated } : t));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update that item.');
    }
  };

  const handleArchive = async (todo, archive) => {
    if (archive && !window.confirm(`Archive "${todo.title}"?`)) return;
    try {
      await todoService.setTodoArchived(todo.id, archive);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not archive that item.');
    }
  };

  const handleWaiting = async (todo, waitingFor, recheckOn) => {
    try {
      await todoService.setTodoWaiting(todo.id, waitingFor, recheckOn);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save that.');
    }
  };

  const handleSnooze = async (todo, followUpAt) => {
    try {
      await todoService.setTodoFollowUp(todo.id, followUpAt);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not set a follow-up date.');
    }
  };

  const matchesSearch = (t) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return t.title.toLowerCase().includes(q) || (t.body || '').toLowerCase().includes(q);
  };
  const visibleActive = (active || []).filter(t => (!hideCompleted || !t.completed_at) && matchesSearch(t));
  // Jenn, 2026-10-27: anything untouched for 14 days, or a waiting item whose
  // recheck date has come, goes to the top so it can't quietly sit.
  const mainView = !showSnoozed && !showArchived;
  const needsLook = mainView ? visibleActive.filter(t => t.needs_look).sort(SORTERS.priority) : [];
  const list = (showSnoozed ? snoozed.filter(matchesSearch) : showArchived ? archived.filter(matchesSearch) : visibleActive.filter(t => !t.needs_look).sort(SORTERS[sortBy]));
  const rowProps = (todo) => ({
    todo, archivedView: showArchived, snoozedView: showSnoozed, highlighted: todo.id === highlightId,
    editing: editingId === todo.id,
    onStartEdit: () => setEditingId(todo.id), onCancelEdit: () => setEditingId(null),
    onSaved: () => { setEditingId(null); load(); },
    onToggleComplete: () => handleToggleComplete(todo), onArchive: (archive) => handleArchive(todo, archive),
    onSnooze: (followUpAt) => handleSnooze(todo, followUpAt),
    onWaiting: (waitingFor, recheckOn) => handleWaiting(todo, waitingFor, recheckOn),
  });

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <AdminNav />
        <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
          <h1 className="font-serif text-2xl font-bold text-reset-brown">To-Dos</h1>
          <button
            onClick={() => { setEditingId(null); setShowNewForm(true); }}
            className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors"
          >
            + Add To-Do
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-5">
          The running list for the team — anyone can pick up any item and see exactly where it stands.
        </p>

        <div className="relative mb-4">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search to-dos by title or details…"
            className="w-full border border-gray-200 rounded-xl pl-9 pr-9 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-reset-brown text-sm"
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        <div className="flex items-center gap-4 mb-4 text-xs flex-wrap">
          {!showArchived && !showSnoozed && (
            <>
              <label className="flex items-center gap-1.5 text-gray-500">
                <input type="checkbox" checked={hideCompleted} onChange={e => setHideCompleted(e.target.checked)} />
                Hide completed
              </label>
              <label className="flex items-center gap-1.5 text-gray-500">
                Sort:
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                  className="border border-gray-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-reset-terracotta">
                  <option value="due">Due date</option>
                  <option value="priority">Priority</option>
                  <option value="newest">Newest first</option>
                </select>
              </label>
            </>
          )}
          <button
            onClick={() => { setShowSnoozed(s => !s); setShowArchived(false); }}
            className={`px-2.5 py-1 rounded-md border ${showSnoozed ? 'border-reset-terracotta text-reset-terracotta' : 'border-gray-200 text-gray-500'}`}
          >
            {showSnoozed ? 'Showing follow-ups' : `Follow-ups later${snoozed.length ? ` (${snoozed.length})` : ''}`}
          </button>
          <button
            onClick={() => { setShowArchived(s => !s); setShowSnoozed(false); }}
            className={`px-2.5 py-1 rounded-md border ${showArchived ? 'border-reset-terracotta text-reset-terracotta' : 'border-gray-200 text-gray-500'}`}
          >
            {showArchived ? 'Showing archived' : 'Show archived'}
          </button>
        </div>

        {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>}

        {showNewForm && (
          <TodoForm
            existing={null}
            onCancel={() => setShowNewForm(false)}
            onSaved={() => { setShowNewForm(false); load(); }}
          />
        )}

        {needsLook.length > 0 && (
          <div className="mb-5 rounded-2xl border border-reset-gold bg-reset-cream p-3">
            <p className="text-sm font-semibold text-reset-brown px-1">Needs a look ({needsLook.length})</p>
            <p className="text-xs text-gray-500 px-1 mb-2">Untouched for 14 days, or a waiting item's check-again date has come. Add a note, change it, or tick it off, and it moves back down.</p>
            <div className="space-y-2">
              {needsLook.map(todo => <TodoRow key={todo.id} {...rowProps(todo)} />)}
            </div>
          </div>
        )}

        {!active ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : list.length === 0 && needsLook.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm px-5 py-8 text-center text-sm text-gray-400">
            {search.trim()
              ? `No to-dos match "${search.trim()}".`
              : showSnoozed ? 'Nothing snoozed for later.' : showArchived ? 'Nothing archived.' : 'Nothing here — add the first item.'}
          </div>
        ) : (
          <div className="space-y-2">
            {list.map(todo => <TodoRow key={todo.id} {...rowProps(todo)} />)}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function TodoRow({ todo, archivedView, snoozedView, highlighted, editing, onStartEdit, onCancelEdit, onSaved, onToggleComplete, onArchive, onSnooze, onWaiting }) {
  const overdue = isOverdue(todo);
  const recurringLabel = RECURRING_LABELS[todo.recurrence_key];
  const [showSnoozeInput, setShowSnoozeInput] = useState(false);
  const [snoozeDate, setSnoozeDate] = useState('');
  const [showWaitingInput, setShowWaitingInput] = useState(false);
  const [waitingFor, setWaitingFor] = useState('');
  const [recheckOn, setRecheckOn] = useState(() => new Date(Date.now() + 7 * 864e5).toLocaleDateString('en-CA'));

  // Editing replaces the whole row with the actual form, in place — clicking
  // the title used to only reveal a read-only view with a small separate
  // "Edit" link buried among other actions, which Jenn flagged as
  // unintuitive: "I would think I can put changes in." Now the click takes
  // you straight to something editable, with a prominent Save button.
  if (editing && !archivedView && !snoozedView) {
    return (
      <div id={`todo-${todo.id}`}>
        <TodoForm existing={todo} onCancel={onCancelEdit} onSaved={onSaved} />
        <div className="bg-white rounded-2xl shadow-sm px-5 py-4">
          <FollowUps todoId={todo.id} />
        </div>
      </div>
    );
  }

  return (
    <div
      id={`todo-${todo.id}`}
      className={`bg-white rounded-2xl shadow-sm px-5 py-4 transition-shadow ${todo.completed_at ? 'opacity-60' : ''} ${highlighted ? 'ring-2 ring-reset-terracotta' : ''}`}
    >
      <div className="flex items-start gap-3">
        {!archivedView && !snoozedView && (
          <button
            onClick={onToggleComplete}
            className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors ${
              todo.completed_at ? 'bg-reset-sage border-reset-sage text-white' : 'border-gray-300 hover:border-reset-terracotta'
            }`}
            title={todo.completed_at ? 'Mark incomplete' : 'Mark complete'}
          >
            {todo.completed_at && '✓'}
          </button>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={!archivedView && !snoozedView ? onStartEdit : undefined}
              className={`text-sm font-semibold text-left ${todo.completed_at ? 'line-through text-gray-400' : 'text-reset-brown'} ${!archivedView && !snoozedView ? 'hover:text-reset-terracotta hover:underline cursor-pointer' : ''}`}
              title={!archivedView && !snoozedView ? 'Click to open and edit' : undefined}
            >
              {todo.title}
            </button>
            {recurringLabel && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-reset-sage/10 text-reset-sage">{recurringLabel}</span>
            )}
            {overdue && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-600">overdue</span>
            )}
            {PRIORITY_BADGE[todo.priority] && (
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${PRIORITY_BADGE[todo.priority]}`}>
                {todo.priority}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {snoozedView && todo.follow_up_at && <>Following up {new Date(todo.follow_up_at).toLocaleString(undefined, { hour12: true })}</>}
            {!snoozedView && todo.due_date && <>Due {formatDue(todo.due_date)}</>}
            {!snoozedView && todo.completed_at && <> · done {new Date(todo.completed_at).toLocaleDateString()}{todo.completed_by_name ? ` by ${todo.completed_by_name}` : ''}</>}
            {!snoozedView && !todo.due_date && !todo.completed_at && <>No due date{todo.look_again_on && !todo.waiting_for ? ` · look again by ${formatDue(String(todo.look_again_on).slice(0, 10))}` : ''}</>}
            {todo.created_by_name && <> · added by {todo.created_by_name}</>}
            {todo.update_count > 0 && <> · {todo.update_count} follow-up{todo.update_count === 1 ? '' : 's'}</>}
          </p>

          {todo.waiting_for && !todo.completed_at && (
            <p className="text-xs text-reset-brown mt-1 bg-reset-creamDark rounded-md px-2 py-1 inline-block">
              Waiting for: {todo.waiting_for}{todo.recheck_on ? ` · check again ${formatDue(String(todo.recheck_on).slice(0, 10))}` : ''}
            </p>
          )}
          {todo.body && (
            <div className="text-xs text-gray-500 mt-1.5 line-clamp-2">{renderRichText(todo.body)}</div>
          )}

          {todo.recurrence_key === 'zoom_unrecognised' && !todo.completed_at && !archivedView && !snoozedView && (
            <WhoIsThis todo={todo} onLinked={onSaved} />
          )}

          {(todo.link_url || todo.attachment_url) && (
            <div className="flex items-center gap-3 text-xs mt-1.5 flex-wrap">
              {todo.link_url && (
                <a href={todo.link_url} target="_blank" rel="noopener noreferrer" className="text-reset-terracotta font-medium hover:underline">
                  🔗 {todo.link_label || 'Link'}
                </a>
              )}
              {todo.attachment_url && (
                <a href={todo.attachment_url} target="_blank" rel="noopener noreferrer" className="text-reset-terracotta font-medium hover:underline">
                  📎 Attachment
                </a>
              )}
            </div>
          )}

          {todo.attachment_urls?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {todo.attachment_urls.map(url => (
                <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                  <img src={url} alt="" className="w-12 h-12 object-cover rounded-lg border border-gray-200 hover:border-reset-terracotta transition-colors" />
                </a>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {!archivedView && !snoozedView && (
              <>
                {todo.completed_at && (
                  <button onClick={onToggleComplete} className="text-xs font-medium text-gray-500 hover:text-reset-terracotta">
                    Mark incomplete
                  </button>
                )}
                <button onClick={() => setShowSnoozeInput(v => !v)} className="text-xs font-medium text-gray-500 hover:text-reset-terracotta">
                  {showSnoozeInput ? 'Cancel' : 'Follow up later…'}
                </button>
                {!todo.completed_at && (todo.waiting_for ? (
                  <button onClick={() => onWaiting(null, null)} className="text-xs font-medium text-gray-500 hover:text-reset-terracotta">No longer waiting</button>
                ) : (
                  <button onClick={() => setShowWaitingInput(v => !v)} className="text-xs font-medium text-gray-500 hover:text-reset-terracotta">
                    {showWaitingInput ? 'Cancel' : 'Waiting on something…'}
                  </button>
                ))}
                <button onClick={() => onArchive(true)} className="text-xs font-medium text-gray-500 hover:text-red-600">Archive</button>
              </>
            )}
            {snoozedView && (
              <button onClick={() => onSnooze(null)} className="text-xs font-medium text-gray-500 hover:text-reset-terracotta">Bring back now</button>
            )}
            {archivedView && (
              <button onClick={() => onArchive(false)} className="text-xs font-medium text-gray-500 hover:text-reset-terracotta">Unarchive</button>
            )}
          </div>
          {showWaitingInput && (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <input value={waitingFor} onChange={e => setWaitingFor(e.target.value)} maxLength={300} placeholder="What it's waiting for (e.g. CB's wording)"
                className="flex-1 min-w-[180px] border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
              <label className="text-xs text-gray-500">Check again
                <input type="date" value={recheckOn} onChange={e => setRecheckOn(e.target.value)}
                  className="ml-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
              </label>
              <button
                onClick={() => { if (waitingFor.trim() && recheckOn) { onWaiting(waitingFor.trim(), recheckOn); setShowWaitingInput(false); } }}
                disabled={!waitingFor.trim() || !recheckOn}
                className="px-3 py-1.5 bg-reset-terracotta text-white text-xs font-medium rounded-lg hover:bg-reset-brown transition-colors disabled:opacity-50"
              >
                Save
              </button>
            </div>
          )}
          {showSnoozeInput && (
            <div className="flex items-center gap-2 mt-2">
              <input type="date" value={snoozeDate} onChange={e => setSnoozeDate(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
              <button
                onClick={() => { if (snoozeDate) { onSnooze(new Date(snoozeDate + 'T09:00:00').toISOString()); setShowSnoozeInput(false); } }}
                disabled={!snoozeDate}
                className="px-3 py-1.5 bg-reset-terracotta text-white text-xs font-medium rounded-lg hover:bg-reset-brown transition-colors disabled:opacity-50"
              >
                Snooze until then
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Jenn, 2026-10-08: members join Zoom under other names ("Paula K" = Paula
// Kennedy). Picking who it is here remembers that name for good, counts their
// past events, and ticks off every Not-recognised To-Do for that name.
function zoomNameFromTitle(title) {
  const m = /:\s*"(.*)"\s*$/.exec(title || '');
  return m ? m[1] : null;
}

function WhoIsThis({ todo, onLinked }) {
  const zoomName = zoomNameFromTitle(todo.title);
  const [members, setMembers] = useState(null);
  const [memberId, setMemberId] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let alive = true;
    todoService.getZoomNameMembers().then(list => { if (alive) setMembers(list); }).catch(() => { if (alive) setMembers([]); });
    return () => { alive = false; };
  }, []);

  if (!zoomName || zoomName === 'no name') return null;

  const save = async () => {
    if (!memberId) return;
    setSaving(true);
    setMessage('');
    try {
      const r = await todoService.linkZoomName(zoomName, memberId);
      setMessage(`Saved. "${zoomName}" is ${r.memberName} from now on.`);
      setTimeout(() => onLinked && onLinked(), 1200);
    } catch (e) {
      setMessage(e.response?.data?.error || 'Could not save that. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
      <label htmlFor={`who-${todo.id}`} className="font-medium text-reset-brown">Who is this?</label>
      <select
        id={`who-${todo.id}`}
        value={memberId}
        onChange={e => setMemberId(e.target.value)}
        disabled={saving || !members}
        className="border border-gray-200 rounded-lg px-2 py-1 bg-white text-reset-brown"
      >
        <option value="">{members ? 'Choose a member…' : 'Loading…'}</option>
        {(members || []).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
      <button
        onClick={save}
        disabled={!memberId || saving}
        className="px-3 py-1 rounded-lg bg-reset-terracotta text-white font-medium disabled:opacity-40"
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
      {message && <span className="text-reset-sage">{message}</span>}
    </div>
  );
}

function FollowUps({ todoId }) {
  const [notes, setNotes] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setNotes(await todoService.getTodoUpdates(todoId)); } catch { setNotes([]); }
  }, [todoId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setSaving(true);
    try {
      await todoService.addTodoUpdate(todoId, draft.trim());
      setDraft('');
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="text-xs font-semibold text-reset-brown mb-1.5">Follow-up notes</p>
      {notes === null ? (
        <p className="text-xs text-gray-400">Loading…</p>
      ) : notes.length === 0 ? (
        <p className="text-xs text-gray-400 mb-2">No follow-up notes yet.</p>
      ) : (
        <div className="space-y-1.5 mb-2">
          {notes.map(n => (
            <div key={n.id} className="text-xs bg-reset-cream rounded-lg px-3 py-2">
              <div className="text-gray-600 [&_p]:mb-0">{renderRichText(n.note)}</div>
              <span className="text-gray-400"> — {n.author_name || 'staff'}, {new Date(n.created_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}
      <form onSubmit={submit} className="flex gap-2 items-start">
        <div className="flex-1">
          <MentionInput
            value={draft}
            onChange={setDraft}
            multiline={false}
            placeholder="Add a status/handoff note… (type @ to tag someone)"
            className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
        </div>
        <button type="submit" disabled={saving || !draft.trim()} className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg text-gray-600 hover:border-reset-terracotta disabled:opacity-50">
          Add
        </button>
      </form>
    </div>
  );
}

function TodoForm({ existing, onCancel, onSaved }) {
  const [title, setTitle] = useState(existing?.title || '');
  const [body, setBody] = useState(existing?.body || '');
  const [dueDate, setDueDate] = useState(existing?.due_date ? existing.due_date.slice(0, 10) : '');
  const [linkUrl, setLinkUrl] = useState(existing?.link_url || '');
  const [linkLabel, setLinkLabel] = useState(existing?.link_label || '');
  const [attachmentUrl, setAttachmentUrl] = useState(existing?.attachment_url || '');
  const [attachmentUrls, setAttachmentUrls] = useState(existing?.attachment_urls || []);
  const [priority, setPriority] = useState(existing?.priority || 'normal');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    const payload = { title: title.trim(), body, dueDate: dueDate || null, linkUrl: linkUrl.trim() || null, linkLabel: linkLabel.trim() || null, attachmentUrl: attachmentUrl.trim() || null, attachmentUrls, priority };
    try {
      if (existing) await todoService.updateTodo(existing.id, payload);
      else await todoService.createTodo(payload);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save this to-do.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm p-5 mb-4 space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
        <input value={title} onChange={e => setTitle(e.target.value)} required autoFocus
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Details</label>
        <RichTextEditor value={body} onChange={setBody} minHeightClass="min-h-[120px]" placeholder="What needs to happen — as much detail as it takes." />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Due date (optional)</label>
          <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Priority</label>
          <select value={priority} onChange={e => setPriority(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta">
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Link label</label>
          <input value={linkLabel} onChange={e => setLinkLabel(e.target.value)} placeholder="e.g. Canva draft"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Link URL</label>
          <input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://…"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Images (up to 7 — e.g. stages of the same screen)</label>
        <MultiImageDropZone urls={attachmentUrls} onChange={setAttachmentUrls} />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Or a link (Drive, Canva, etc.)</label>
        <input value={attachmentUrl} onChange={e => setAttachmentUrl(e.target.value)} placeholder="https://drive.google.com/…"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex items-center gap-3">
        <button type="submit" disabled={saving || !title.trim()} className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50">
          {saving ? 'Saving…' : existing ? 'Save changes' : 'Add to-do'}
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-gray-500 hover:text-reset-brown">Cancel</button>
      </div>
    </form>
  );
}
